import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Globe, Instagram, Phone, Mail, Loader2, Sparkles,
  RotateCcw, XCircle, CalendarClock, ChevronDown, Pencil, Tag, Trash2,
  AlertTriangle, ClipboardPaste,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { StatusBadge } from '../components/ai-lead-scanner/StatusBadge';
import { PriorityBadge } from '../components/ai-lead-scanner/PriorityBadge';
import { TemperatureBadge } from '../components/ai-lead-scanner/TemperatureBadge';
import { ScoreBreakdown } from '../components/ai-lead-scanner/ScoreBreakdown';
import { WebsiteAnalysisSummary } from '../components/ai-lead-scanner/WebsiteAnalysisSummary';
import { MessageCard } from '../components/ai-lead-scanner/MessageCard';
import { MarkFollowUpDialog } from '../components/ai-lead-scanner/MarkFollowUpDialog';
import { EditLeadModal } from '../components/ai-lead-scanner/EditLeadModal';
import { DiscardLeadsDialog } from '../components/ai-lead-scanner/DiscardLeadsDialog';
import { ManualContentModal } from '../components/ai-lead-scanner/ManualContentModal';
import { ActiveFollowUpsCard } from '../components/ai-lead-scanner/ActiveFollowUpsCard';

// Umbral de aviso de sugerencia de descarte (2026-07-08) — mismo valor que
// FOLLOW_UP_LIMIT en backend/ai_lead_scanner/models.py. Sin constante
// compartida entre frontend/backend en este módulo (no existe ese
// mecanismo hoy en AdamSched, ver p.ej. los 21 días de reprogramación,
// también hardcodeados solo en el backend).
const FOLLOW_UP_LIMIT = 3;
const TERMINAL_STATUSES = ['CUSTOMER', 'NOT_INTERESTED', 'DISCARDED'];

// Ficha de lead — Commit 14, sub-entrega 2/3. Página propia (no drawer),
// mismo patrón que ClientProfile.js — la única otra entidad comparable
// en complejidad ya establece ese precedente en AdamSched.
export function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { api } = useAuth();
  const { t } = useTranslation();

  // Restaura los filtros de la lista al volver — location.state.fromSearch
  // lo fija LeadTable.jsx al navegar aquí desde una fila. Sin state (p.ej.
  // llegada directa desde el enlace del digest diario por email,
  // daily_digest.py:_LEAD_DETAIL_URL_BASE), cae a la lista sin filtros,
  // igual que el comportamiento de siempre.
  const backUrl = `/internal/ai-lead-scanner${location.state?.fromSearch || ''}`;

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // nombre de la acción en curso, o null
  // Bump tras marcar un seguimiento nuevo — ActiveFollowUpsCard tiene su
  // propio fetch a GET /leads/{id}/events, separado del fetchLead() de
  // arriba (los eventos no viajan dentro de LeadResponse).
  const [eventsRefreshKey, setEventsRefreshKey] = useState(0);
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [manualContentModalOpen, setManualContentModalOpen] = useState(false);

  const fetchLead = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leads/${id}`);
      setLead(res.data);
    } catch (error) {
      console.error('Failed to fetch lead:', error);
      toast.error(t('aiLeadScanner.leadDetail.loadError'));
      setLead(null);
    } finally {
      setLoading(false);
    }
  }, [api, id, t]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  const runAction = async (name, fn) => {
    setActionLoading(name);
    try {
      await fn();
      toast.success(t('aiLeadScanner.leadDetail.actionSuccess'));
      await fetchLead();
    } catch (error) {
      console.error(`Action ${name} failed:`, error);
      toast.error(t('aiLeadScanner.leadDetail.actionError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Bump de eventsRefreshKey también aquí: CONTACTED_WHATSAPP/EMAIL/CALL
  // puede programar un seguimiento automático a 21 días (leads_crud.py),
  // que ActiveFollowUpsCard no reflejaba hasta remontar la página (bug
  // real reportado 2026-07-13) — sin esto, la card solo se refrescaba tras
  // el diálogo manual de "Marcar seguimiento".
  const handleMarkContacted = (status) =>
    runAction('contact', () => api.post(`/leads/${id}/status`, { status }))
      .then(() => setEventsRefreshKey((k) => k + 1));

  const handleMarkNotInterested = () =>
    runAction('notInterested', () => api.post(`/leads/${id}/status`, { status: 'NOT_INTERESTED' }));

  // Borrado suave — status=DISCARDED (excluded_reason ya lo fija el
  // backend, Commit posterior a T-64). Con diálogo de confirmación
  // explícito, mismo patrón que la versión en lote de LeadScannerPage.js.
  const handleDiscardConfirm = () => {
    runAction('discard', () => api.post(`/leads/${id}/status`, { status: 'DISCARDED' }))
      .then(() => setDiscardDialogOpen(false));
  };

  const handleRegenerateMessages = () =>
    runAction('regenerate', () => api.post(`/leads/${id}/regenerate-messages`));

  const handleReanalyze = () =>
    runAction('reanalyze', () => api.post(`/leads/${id}/analyze`));

  const handleFollowUpConfirm = (isoDate, note) => {
    runAction('followUp', () => api.post(`/leads/${id}/follow-up`, { next_follow_up_at: isoDate, note }))
      .then(() => {
        setFollowUpDialogOpen(false);
        setEventsRefreshKey((k) => k + 1);
      });
  };

  // Un solo clic en "Copiar" copia Y registra POST /leads/{id}/mark-message-copied
  // (Commit 13) — sin esperar respuesta antes de refrescar, no es una
  // acción crítica que necesite bloquear la UI.
  const handleMessageCopied = (messageType) => {
    api.post(`/leads/${id}/mark-message-copied`, { message_type: messageType }).catch((err) => {
      console.error('Failed to log message copied:', err);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="p-6 text-center">
        <p className="text-zinc-500 mb-4">{t('aiLeadScanner.leadDetail.notFound')}</p>
        <Button variant="outline" onClick={() => navigate(backUrl)}>
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          {t('aiLeadScanner.leadDetail.back')}
        </Button>
      </div>
    );
  }

  const pack = lead.message_pack;
  const analysis = lead.website_analysis;
  const followUpCount = lead.follow_up_count || 0;
  const isTerminal = TERMINAL_STATUSES.includes(lead.status);
  const showDiscardSuggestion = followUpCount >= FOLLOW_UP_LIMIT && !isTerminal;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto h-full overflow-y-auto">
      <Button variant="ghost" size="sm" onClick={() => navigate(backUrl)} className="mb-3 text-zinc-400">
        <ArrowLeft className="w-4 h-4 mr-1.5" />
        {t('aiLeadScanner.leadDetail.back')}
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">{lead.business_name}</h1>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <StatusBadge status={lead.status} />
            <PriorityBadge priority={lead.priority} />
            <TemperatureBadge temperature={lead.temperature} />
            <span className="text-xs text-zinc-500">
              {t('aiLeadScanner.leadDetail.followUpCount', { count: followUpCount, limit: FOLLOW_UP_LIMIT })}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={actionLoading === 'contact'}>
                {actionLoading === 'contact' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {t('aiLeadScanner.leadDetail.markContacted')}
                <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleMarkContacted('CONTACTED_WHATSAPP')}>
                {t('aiLeadScanner.leadDetail.markContactedWhatsapp')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleMarkContacted('CONTACTED_EMAIL')}>
                {t('aiLeadScanner.leadDetail.markContactedEmail')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleMarkContacted('CONTACTED_CALL')}>
                {t('aiLeadScanner.leadDetail.markContactedCall')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Cambiar estado — desplegable genérico para los estados sin
              botón rápido propio (DEMO_PENDING, DEMO_SCHEDULED,
              EVALUATING_PROPOSAL, CUSTOMER). Reutiliza handleMarkContacted()
              tal cual — ya es un "set status genérico" internamente, solo
              cambia qué status se le pasa. Sin endpoint ni lógica nueva. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={actionLoading === 'contact'}>
                {actionLoading === 'contact' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Tag className="w-4 h-4 mr-1.5" />}
                {t('aiLeadScanner.leadDetail.changeStatus')}
                <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleMarkContacted('DEMO_PENDING')}>
                {t('aiLeadScanner.status.DEMO_PENDING')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleMarkContacted('DEMO_SCHEDULED')}>
                {t('aiLeadScanner.status.DEMO_SCHEDULED')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleMarkContacted('EVALUATING_PROPOSAL')}>
                {t('aiLeadScanner.status.EVALUATING_PROPOSAL')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleMarkContacted('CUSTOMER')}>
                {t('aiLeadScanner.status.CUSTOMER')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="outline" size="sm" onClick={() => setFollowUpDialogOpen(true)}>
            <CalendarClock className="w-4 h-4 mr-1.5" />
            {t('aiLeadScanner.leadDetail.markFollowUp')}
          </Button>

          <Button
            variant="outline" size="sm"
            disabled={actionLoading === 'notInterested'}
            onClick={handleMarkNotInterested}
          >
            {actionLoading === 'notInterested'
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <XCircle className="w-4 h-4 mr-1.5" />}
            {t('aiLeadScanner.leadDetail.markNotInterested')}
          </Button>

          <Button
            variant="outline" size="sm"
            onClick={() => setDiscardDialogOpen(true)}
            data-testid="open-discard-dialog"
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            {t('aiLeadScanner.leadDetail.markDiscard')}
          </Button>

          <Button
            variant="outline" size="sm"
            disabled={actionLoading === 'regenerate' || !analysis}
            onClick={handleRegenerateMessages}
          >
            {actionLoading === 'regenerate'
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <RotateCcw className="w-4 h-4 mr-1.5" />}
            {t('aiLeadScanner.leadDetail.regenerateMessages')}
          </Button>

          <Button
            size="sm"
            disabled={actionLoading === 'reanalyze'}
            onClick={handleReanalyze}
          >
            {actionLoading === 'reanalyze'
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <Sparkles className="w-4 h-4 mr-1.5" />}
            {t('aiLeadScanner.leadDetail.reanalyze')}
          </Button>
        </div>
      </div>

      {/* Solo cuando el último fallo fue de scraping (ScraperError) — pegar
          texto no ayuda si lo que falló fue el análisis LLM, el scoring u
          otra etapa, problemas distintos donde esta opción no aplica. */}
      {lead.status === 'ERROR' && lead.last_error_stage === 'scrape' && (
        <div className="flex items-center justify-between gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-400">{t('aiLeadScanner.leadDetail.pasteManualContentDescription')}</p>
          </div>
          <Button
            variant="outline" size="sm" className="flex-shrink-0"
            onClick={() => setManualContentModalOpen(true)}
            data-testid="open-manual-content-modal"
          >
            <ClipboardPaste className="w-4 h-4 mr-1.5" />
            {t('aiLeadScanner.leadDetail.pasteManualContent')}
          </Button>
        </div>
      )}

      {/* Sugerencia de descarte tras alcanzar el límite de seguimientos —
          mismo patrón visual que el aviso de "pegar contenido manualmente"
          (arriba): franja ámbar + botón. Nunca descarte automático, el
          botón solo abre el mismo diálogo de confirmación que ya usa la
          acción "Descartar" del header. */}
      {showDiscardSuggestion && (
        <div className="flex items-center justify-between gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-400">
              {t('aiLeadScanner.leadDetail.discardSuggestionDescription', { limit: FOLLOW_UP_LIMIT })}
            </p>
          </div>
          <Button
            variant="outline" size="sm" className="flex-shrink-0"
            onClick={() => setDiscardDialogOpen(true)}
            data-testid="open-discard-suggestion-dialog"
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            {t('aiLeadScanner.leadDetail.markDiscard')}
          </Button>
        </div>
      )}

      {/* Perfil */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-zinc-200">{t('aiLeadScanner.leadDetail.profile')}</h2>
          <Button variant="ghost" size="sm" onClick={() => setEditModalOpen(true)} data-testid="edit-lead-button">
            <Pencil className="w-3.5 h-3.5 mr-1.5" />
            {t('aiLeadScanner.leadDetail.edit')}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          {lead.address && (
            <div className="flex items-center gap-2 text-zinc-400">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{lead.address}{lead.city ? `, ${lead.city}` : ''}{lead.province ? ` (${lead.province})` : ''}</span>
            </div>
          )}
          {lead.phone && (
            <div className="flex items-center gap-2 text-zinc-400">
              <Phone className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{lead.phone}</span>
            </div>
          )}
          {lead.email && (
            <div className="flex items-center gap-2 text-zinc-400">
              <Mail className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{lead.email}</span>
            </div>
          )}
          {lead.website_url && (
            <div className="flex items-center gap-2 text-zinc-400">
              <Globe className="w-3.5 h-3.5 flex-shrink-0" />
              <a href={lead.website_url} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline truncate">
                {lead.website_url}
              </a>
            </div>
          )}
          {lead.instagram_url && (
            <div className="flex items-center gap-2 text-zinc-400">
              <Instagram className="w-3.5 h-3.5 flex-shrink-0" />
              <a href={lead.instagram_url} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline truncate">
                {lead.instagram_url}
              </a>
            </div>
          )}
        </div>
        {lead.needs_website && (
          <span className="inline-block mt-2 text-xs bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded-full px-2.5 py-1">
            {t('aiLeadScanner.leadDetail.needsWebsite')}
          </span>
        )}
        {lead.notes && (
          <p className="text-sm text-zinc-400 mt-3 border-t border-zinc-800 pt-3">
            <span className="font-medium text-zinc-300">{t('aiLeadScanner.leadDetail.notes')}:</span> {lead.notes}
          </p>
        )}
      </div>

      {/* Seguimientos activos — solo lectura, eventos FOLLOW_UP_SET ya
          creados por "Marcar seguimiento" arriba. Sin acción propia en esta
          card (alcance reducido 2026-07-09, ver docs/adam-intelligence-spec.md). */}
      <div className="mb-4">
        <ActiveFollowUpsCard leadId={id} refreshKey={eventsRefreshKey} />
      </div>

      {/* Score */}
      <div className="mb-4">
        <ScoreBreakdown
          score={lead.score}
          priority={lead.priority}
          scoreReason={lead.score_reason}
          signalsPositive={lead.signals_positive}
          signalsNegative={lead.signals_negative}
        />
      </div>

      {/* Análisis de la web */}
      <div className="mb-4">
        <WebsiteAnalysisSummary analysis={analysis} />
      </div>

      {/* Mensajes generados */}
      {pack ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <MessageCard
            title={t('aiLeadScanner.messageCard.whatsappInitial')}
            body={pack.whatsapp_initial?.body}
            channel="whatsapp"
            phone={lead.phone}
            messageType="whatsapp_initial"
            onCopy={handleMessageCopied}
          />
          <MessageCard
            title={t('aiLeadScanner.messageCard.emailInitial')}
            body={pack.email_initial?.body}
            subject={pack.email_initial?.subject}
            channel="email"
            email={lead.email}
            wordCount={pack.email_initial?.word_count}
            exceedsWordLimit={pack.exceeds_word_limit}
            messageType="email_initial"
            onCopy={handleMessageCopied}
          />
          <MessageCard
            title={t('aiLeadScanner.messageCard.followUp1')}
            body={pack.follow_up_1?.body}
            subject={pack.follow_up_1?.subject}
            channel={pack.follow_up_1?.channel}
            phone={lead.phone}
            email={lead.email}
            messageType="follow_up_1"
            onCopy={handleMessageCopied}
          />
          <MessageCard
            title={t('aiLeadScanner.messageCard.followUp2')}
            body={pack.follow_up_2?.body}
            subject={pack.follow_up_2?.subject}
            channel={pack.follow_up_2?.channel}
            phone={lead.phone}
            email={lead.email}
            messageType="follow_up_2"
            onCopy={handleMessageCopied}
          />
          <MessageCard
            title={t('aiLeadScanner.messageCard.callScript')}
            body={pack.call_script}
            channel={null}
            messageType="call_script"
            onCopy={handleMessageCopied}
          />
          {/* internal_notes: sin onCopy — no es un message_type válido para
              mark-message-copied (MESSAGE_TYPES en el backend no lo incluye,
              a propósito: son notas de Marta para sí misma, nunca se envían
              al lead, así que no tiene sentido auditarlas como "copiadas
              para contactar"). Copia al portapapeles funciona igual. */}
          <MessageCard
            title={t('aiLeadScanner.messageCard.internalNotes')}
            body={pack.internal_notes}
            channel={null}
            messageType="internal_notes"
          />
        </div>
      ) : (
        <p className="text-sm text-zinc-500">{t('aiLeadScanner.leadDetail.noMessagesYet')}</p>
      )}

      <MarkFollowUpDialog
        open={followUpDialogOpen}
        onOpenChange={setFollowUpDialogOpen}
        onConfirm={handleFollowUpConfirm}
        submitting={actionLoading === 'followUp'}
      />

      <EditLeadModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        lead={lead}
        onSaved={fetchLead}
      />

      <DiscardLeadsDialog
        open={discardDialogOpen}
        onOpenChange={setDiscardDialogOpen}
        count={1}
        onConfirm={handleDiscardConfirm}
        confirming={actionLoading === 'discard'}
      />

      <ManualContentModal
        open={manualContentModalOpen}
        onOpenChange={setManualContentModalOpen}
        leadId={id}
        onAnalyzed={fetchLead}
      />
    </div>
  );
}
